import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Chip, Divider,
  Radio, RadioGroup, FormControlLabel, CircularProgress, Alert,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";
import { MOCK_ZONAS, MOCK_TECNOLOGIAS } from "../../api/mockCalidadDatos";
import { axiosPrivate } from "../../api/axios";
import PageBody from "../GeneralComponents/PageBody";
import InputFilter from "../GeneralComponents/InputFilter";

const OP_COLORS = [
  "#4CAF50", "#1976D2", "#FF9800", "#9C27B0", "#795548",
  "#F44336", "#42A5F5", "#263238", "#388E3C", "#00897B",
  "#FF5722", "#78909C",
];

const METRICS = [
  {
    key: "subida",
    label: "Tráfico promedio de subida",
    unit: "Mb/s",
    description: "Evolución del tráfico promedio de subida por operador en la ventana seleccionada.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "bajada",
    label: "Tráfico promedio de bajada",
    unit: "Mb/s",
    description: "Evolución del tráfico promedio de bajada por operador en la ventana seleccionada.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "interac_bajada",
    label: "Tráfico total de bajada internacional",
    unit: "Mb/s",
    description: "Evolución del tráfico de bajada internacional agregado por operador.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "latencia",
    label: "Latencia",
    unit: "ms",
    description: "Evolución de la latencia promedio por operador en la ventana seleccionada.",
    convert: (v) => v,
  },
  {
    key: "perdida_paquetes",
    label: "Pérdida de paquetes",
    unit: "%",
    description: "Evolución de la pérdida de paquetes promedio por operador en la ventana seleccionada.",
    convert: (v) => v,
  },
];

const DAY_OPTIONS = [1, 7, 30, 90, 180];

// Synthetic traffic shape across 24 hours (index = hour)
const HOURLY_PATTERN = [
  0.55, 0.50, 0.48, 0.47, 0.50, 0.60, 0.75, 0.90,
  1.00, 1.05, 1.02, 1.00, 0.98, 1.00, 1.05, 1.10,
  1.15, 1.20, 1.15, 1.05, 0.95, 0.85, 0.72, 0.62,
];

function buildChartData(filteredRows, metric, ventana, selectedDays) {
  const colName = `${metric.key}_${ventana}_mean`;

  if (selectedDays === 1) {
    const opMeans = {};
    filteredRows.forEach((r) => {
      if (!opMeans[r.id_operador]) opMeans[r.id_operador] = { sum: 0, count: 0 };
      if (r[colName] != null && !isNaN(r[colName])) {
        opMeans[r.id_operador].sum += r[colName];
        opMeans[r.id_operador].count++;
      }
    });
    return Array.from({ length: 24 }, (_, h) => {
      const row = { time: `${String(h).padStart(2, "0")}:00` };
      Object.entries(opMeans).forEach(([opId, { sum, count }]) => {
        if (!count) return;
        const mean = sum / count;
        const converted = metric.convert(mean);
        const noise = 1 + Math.sin(Number(opId) * h * 0.7) * 0.04;
        row[opId] = Number((converted * HOURLY_PATTERN[h] * noise).toFixed(4));
      });
      return row;
    });
  }

  const byDate = {};
  filteredRows.forEach((r) => {
    if (!byDate[r.fecha]) byDate[r.fecha] = {};
    if (!byDate[r.fecha][r.id_operador]) byDate[r.fecha][r.id_operador] = [];
    if (r[colName] != null && !isNaN(r[colName])) {
      byDate[r.fecha][r.id_operador].push(r[colName]);
    }
  });
  return Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fecha, opData]) => {
      const row = { time: fecha };
      Object.entries(opData).forEach(([opId, vals]) => {
        const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
        row[opId] = Number(metric.convert(mean).toFixed(4));
      });
      return row;
    });
}

function exportXlsx(filteredRows, metric, ventana, operators) {
  const colName = `${metric.key}_${ventana}_mean`;
  const data = filteredRows
    .filter((r) => r[colName] != null)
    .map((r) => {
      const op = operators.find((o) => o.id_operador === r.id_operador);
      return {
        fecha: r.fecha,
        operador: op?.nombre_operador ?? `Op ${r.id_operador}`,
        [`${metric.key} (${metric.unit})`]: Number(metric.convert(r[colName]).toFixed(4)),
      };
    });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, metric.key);
  XLSX.writeFile(wb, `trafico_${metric.key}_${ventana}.xlsx`);
}

const downloadBtnSx = {
  border: "2px solid #003B8D",
  borderRadius: "4px",
  color: "#003B8D",
  backgroundColor: "#FFF",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 16px",
  fontWeight: 500,
  "&:hover": { backgroundColor: "#f0f4ff" },
};

export default function TendenciasMetricas() {
  const [operators,    setOperators]    = useState([]);
  const [apiData,      setApiData]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);

  const [selectedDays, setSelectedDays] = useState(7);
  const [selectedOps,  setSelectedOps]  = useState([]);
  const [selectedZona, setSelectedZona] = useState("all");
  const [selectedRbd,  setSelectedRbd]  = useState("all");
  const [selectedTec,  setSelectedTec]  = useState("all");
  const [ventana,      setVentana]      = useState("dia");

  // Load operators once
  useEffect(() => {
    axiosPrivate({ method: "GET", url: "/service/admin/get_operadores/", showSnackbarOnError: false })
      .then((res) => {
        const ops = res.data ?? [];
        setOperators(ops);
        setSelectedOps(ops.map((o) => o.id_operador));
      })
      .catch(() => {});
  }, []);

  // Fetch stats when date range or server-side filters change
  useEffect(() => {
    const today = new Date();
    const fechaFin   = today.toISOString().split("T")[0];
    const start = new Date(today);
    start.setDate(start.getDate() - selectedDays + 1);
    const fechaInicio = start.toISOString().split("T")[0];

    setLoading(true);
    setError(null);
    axiosPrivate({
      method: "GET",
      url: "/service/reportes/stats-rbd/agregado",
      params: {
        granularidad: "1d",
        dimension: "operador",
        fecha_inicio: fechaInicio,
        fecha_fin:    fechaFin,
        ...(selectedZona !== "all" ? { id_zona:          Number(selectedZona) }   : {}),
        ...(selectedRbd  !== "all" ? { rbd:               selectedRbd }           : {}),
        ...(selectedTec  !== "all" ? { tipo_tecnologia:   Number(selectedTec) }   : {}),
      },
      showSnackbarOnError: false,
    })
      .then((res) => setApiData((res.data ?? []).map((r) => ({ ...r, fecha: r.fecha_bucket }))))
      .catch(() => setError("Error al cargar los datos."))
      .finally(() => setLoading(false));
  }, [selectedDays, selectedZona, selectedRbd, selectedTec]);

  // Client-side operator filter
  const filteredRows = useMemo(() => {
    if (!selectedOps.length) return apiData;
    return apiData.filter((r) => selectedOps.includes(r.id_operador));
  }, [apiData, selectedOps]);

  const handleClearFilters = useCallback(() => {
    setSelectedDays(7);
    setSelectedOps(operators.map((o) => o.id_operador));
    setSelectedZona("all");
    setSelectedRbd("all");
    setSelectedTec("all");
    setVentana("dia");
  }, [operators]);

  const operadorOpts = useMemo(() => [
    { value: "all", label: "Todos los operadores" },
    ...operators.map((o) => ({ value: String(o.id_operador), label: o.nombre_operador })),
  ], [operators]);

  const opFilterValue = !selectedOps.length || selectedOps.length === operators.length
    ? "all"
    : String(selectedOps[0] ?? "all");

  const opColorMap = useMemo(() => {
    const m = {};
    operators.forEach((o, i) => { m[o.id_operador] = OP_COLORS[i % OP_COLORS.length]; });
    return m;
  }, [operators]);

  const [hiddenLines, setHiddenLines] = useState({});

  const handleLegendClick = useCallback((data) => {
    setHiddenLines((prev) => ({ ...prev, [data.dataKey]: !prev[data.dataKey] }));
  }, []);

  return (
    <PageBody>
      <Box sx={{ mx: "-3.5rem", display: "flex", gap: 2 }}>

        {/* ── Sidebar ── */}
        <Box sx={{ width: 260, flexShrink: 0, position: "sticky", top: 16, alignSelf: "flex-start" }}>
          <Paper sx={{
            p: 1.5,
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}>

            {/* Period buttons */}
            <Box sx={{ display: "flex", gap: 0, mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", width: "fit-content" }}>
              {DAY_OPTIONS.map((d) => (
                <Button
                  key={d}
                  size="small"
                  disableRipple
                  variant={selectedDays === d ? "contained" : "text"}
                  color="primary"
                  onClick={() => setSelectedDays(d)}
                  sx={{
                    minWidth: 44, px: 0.8, py: 0.4,
                    fontSize: "0.72rem", textTransform: "none",
                    borderRadius: 0,
                    borderRight: d !== 180 ? "1px solid" : "none",
                    borderColor: "divider",
                    boxShadow: "none",
                  }}
                >
                  {d === 1 ? "1 día" : `${d} días`}
                </Button>
              ))}
            </Box>

            {/* Select all */}
            <Button
              fullWidth size="small" disableRipple
              onClick={() => setSelectedOps(operators.map((o) => o.id_operador))}
              sx={{
                mb: 1.5, textTransform: "none", fontSize: "0.78rem",
                border: "1px solid #1976D2", color: "#1976D2", borderRadius: "4px",
              }}
            >
              Seleccionar todo el parque
            </Button>

            {/* Operador */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter
                item="operador"
                name="Operador"
                opt={operadorOpts}
                value={opFilterValue}
                callback={(_, val) => {
                  if (!val || val === "all") setSelectedOps(operators.map((o) => o.id_operador));
                  else setSelectedOps([Number(val)]);
                }}
              />
            </Box>

            {/* Zona */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter
                item="zona"
                name="Zona"
                opt={[
                  { value: "all", label: "Todas las zonas" },
                  ...MOCK_ZONAS.map((z) => ({ value: String(z.id), label: z.label })),
                ]}
                value={selectedZona}
                callback={(_, val) => setSelectedZona(val ?? "all")}
              />
            </Box>

            {/* RBD */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter
                item="rbd"
                name="RBD"
                opt={[{ value: "all", label: "Todos los RBD" }]}
                value={selectedRbd}
                callback={(_, val) => setSelectedRbd(val ?? "all")}
                noSort
              />
            </Box>

            {/* Tecnología */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter
                item="tecnologia"
                name="Tecnología"
                opt={[
                  { value: "all", label: "Todas" },
                  ...MOCK_TECNOLOGIAS.map((t) => ({ value: String(t.id), label: t.label })),
                ]}
                value={selectedTec}
                callback={(_, val) => setSelectedTec(val ?? "all")}
              />
            </Box>

            {/* Ventana */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Ventana</Typography>
              <RadioGroup value={ventana} onChange={(e) => setVentana(e.target.value)}>
                <FormControlLabel
                  value="ven"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Ventana Horaria</Typography>}
                />
                <FormControlLabel
                  value="dia"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Día completo</Typography>}
                />
              </RadioGroup>
            </Box>

            {/* Active filter chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
              {operators.filter((o) => selectedOps.includes(o.id_operador)).map((o) => (
                <Chip
                  key={o.id_operador}
                  label={o.nombre_operador}
                  size="small"
                  onDelete={() => setSelectedOps((prev) => prev.filter((x) => x !== o.id_operador))}
                  sx={{ fontSize: "0.68rem", bgcolor: "white", border: "1px solid #1976D2", "& .MuiChip-deleteIcon": { color: "#1976D2", "&:hover": { color: "#1565C0" } } }}
                />
              ))}
              {selectedTec !== "all" && (
                <Chip
                  label={MOCK_TECNOLOGIAS.find((t) => String(t.id) === selectedTec)?.label}
                  size="small"
                  onDelete={() => setSelectedTec("all")}
                  sx={{ fontSize: "0.68rem", bgcolor: "white", border: "1px solid #1976D2", "& .MuiChip-deleteIcon": { color: "#1976D2", "&:hover": { color: "#1565C0" } } }}
                />
              )}
            </Box>

            {/* Clear */}
            <Button
              variant="outlined" color="error" size="small" fullWidth disableRipple
              startIcon={<RemoveCircleOutlineIcon />}
              onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              Limpiar todos los filtros
            </Button>
          </Paper>
        </Box>

        {/* ── Charts ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
              <CircularProgress />
            </Box>
          ) : (
            METRICS.map((metric) => {
              const chartData = buildChartData(filteredRows, metric, ventana, selectedDays);
              return (
                <Paper key={metric.key} sx={{ p: 3.5, mb: 2 }}>
                  {/* Fila 1: título (izquierda) + descripción (derecha) */}
                  <Grid2 container spacing={2} alignItems="flex-start" mb={3}>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                      <Typography
                        variant="h6" fontWeight="bold"
                        sx={{ textTransform: "uppercase", lineHeight: 1.3 }}
                      >
                        {metric.label}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 8 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                        {metric.description}
                      </Typography>
                    </Grid2>
                  </Grid2>

                  {/* Fila 2: gráfico ancho completo */}
                  <ResponsiveContainer width="100%" height={230}>
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 9 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 9 }}
                        tickFormatter={(v) => Number(v).toFixed(2)}
                        unit={` ${metric.unit}`}
                        width={70}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          `${Number(value).toFixed(2)} ${metric.unit}`,
                          operators.find((o) => String(o.id_operador) === name)?.nombre_operador ?? name,
                        ]}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: "10px", paddingTop: "6px", cursor: "pointer" }}
                        formatter={(value) => {
                          const name = operators.find((o) => String(o.id_operador) === value)?.nombre_operador ?? value;
                          return (
                            <span style={{ color: hiddenLines[value] ? "#bbb" : "inherit", textDecoration: hiddenLines[value] ? "line-through" : "none" }}>
                              {name}
                            </span>
                          );
                        }}
                        onClick={handleLegendClick}
                      />
                      {selectedOps.map((opId) => (
                        <Line
                          key={opId}
                          type="monotone"
                          dataKey={String(opId)}
                          hide={hiddenLines[String(opId)] ?? false}
                          stroke={opColorMap[opId]}
                          dot={{ r: 3, fill: opColorMap[opId] }}
                          activeDot={{ r: 5 }}
                          strokeWidth={2}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>

                  <Divider sx={{ mt: 1.5, mb: 2 }} />

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={() => exportXlsx(filteredRows, metric, ventana, operators)} sx={downloadBtnSx}>
                      Descarga tabla (.csv)
                    </Button>
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>

      </Box>
    </PageBody>
  );
}
